/**
 * ShareExpiryEditor — inline editor for a Share's expires_at, designed
 * to drop into the existing "expires {date}" text spans on the DCP
 * Distribution expand panel. Replaces a static label with a clickable
 * button that opens a small popover with a date input.
 *
 * Ship 1 of the v10.21.x DCP UX refactor (operator feedback
 * 2026-06-13: "I want to be able to change expiration dates on KDM's
 * and DCP's."). The "edit expiration dates" surface is a Share-level
 * concern — KDM XML payloads are cryptographically signed and cannot
 * have their valid_from/valid_to edited; what the operator actually
 * needs to edit is the public-link expiry on the Share row that wraps
 * the KDM (or wraps the DCP package). This component handles both.
 *
 * Backend: PATCH /api/delivery/shares/{share_id}/expires already
 * shipped at v9.x; this component is the missing UI.
 */
import { useState } from 'react';
import {
  Box, Typography, Popover, Button, TextField, Tooltip, IconButton,
} from '@mui/material';
import { EditCalendar as EditCalendarIcon } from '@mui/icons-material';
import api from '../../services/api';

function toLocalInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ShareExpiryEditor({
  share,
  onChanged,
  size = 'small',
  textOnly = false,
}) {
  const [anchor, setAnchor] = useState(null);
  const [draft, setDraft] = useState(toLocalInput(share?.expires_at));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  if (!share) return null;

  const open = (e) => {
    e.stopPropagation();
    setDraft(toLocalInput(share.expires_at));
    setError(null);
    setAnchor(e.currentTarget);
  };
  const close = (e) => {
    if (e) e.stopPropagation();
    setAnchor(null);
  };

  const save = async (e) => {
    if (e) e.stopPropagation();
    if (!draft) {
      setError('Pick a date');
      return;
    }
    // Convert local-datetime input back to ISO with current tz offset.
    const isoLocal = new Date(draft).toISOString();
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/api/delivery/shares/${share.id}/expires`, {
        expires_at: isoLocal,
      });
      close();
      if (onChanged) onChanged(isoLocal);
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const label = share.expires_at ? `expires ${fmtDate(share.expires_at)}` : 'no expiry set';

  // textOnly: render as a clickable text span without an icon button —
  // for inlining inside existing "sent X · expires Y" Typography rows
  // without bulking up the visual weight.
  if (textOnly) {
    return (
      <>
        <Tooltip title={share.revoked_at ? 'Share is revoked — expiry cannot be edited' : 'Click to change expiration date'}>
          <Box
            component="span"
            onClick={share.revoked_at ? undefined : open}
            sx={{
              cursor: share.revoked_at ? 'default' : 'pointer',
              textDecoration: share.revoked_at ? 'none' : 'underline',
              textDecorationStyle: 'dotted',
              textUnderlineOffset: 2,
              color: 'inherit',
              fontSize: 'inherit',
            }}
          >
            {label}
          </Box>
        </Tooltip>
        <ExpiryPopover
          anchor={anchor}
          onClose={close}
          draft={draft}
          setDraft={setDraft}
          onSave={save}
          saving={saving}
          error={error}
        />
      </>
    );
  }

  return (
    <>
      <Tooltip title={share.revoked_at ? 'Share is revoked — expiry cannot be edited' : 'Change expiration date'}>
        <span>
          <IconButton size={size} disabled={!!share.revoked_at} onClick={open}>
            <EditCalendarIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </span>
      </Tooltip>
      <ExpiryPopover
        anchor={anchor}
        onClose={close}
        draft={draft}
        setDraft={setDraft}
        onSave={save}
        saving={saving}
        error={error}
      />
    </>
  );
}

function ExpiryPopover({ anchor, onClose, draft, setDraft, onSave, saving, error }) {
  return (
    <Popover
      open={Boolean(anchor)}
      anchorEl={anchor}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      onClick={(e) => e.stopPropagation()}
    >
      <Box sx={{ p: 1.5, minWidth: 260 }}>
        <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.75 }}>
          New expiration date
        </Typography>
        <TextField
          type="datetime-local"
          size="small"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
        />
        {error && (
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'error.main', fontSize: '0.7rem' }}>
            {error}
          </Typography>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5, mt: 1 }}>
          <Button size="small" onClick={onClose}>Cancel</Button>
          <Button size="small" variant="contained" onClick={onSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Box>
    </Popover>
  );
}
